import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const PlayerCard = ({ player, position, clubColor }) => {
  const navigate = useNavigate();

  const getStatLabel = (position) => {
    switch (position) {
      case "GOALKEEPERS":
        return ["saves", "cleanSheets", "SAVES", "CLEAN SHEETS"];
      case "DEFENDERS":
        return ["tackles", "assists", "TACKLES", "ASSISTS"];
      default:
        return ["assists", "tackles", "ASSISTS", "TACKLES"];
    }
  };

  const [stat1Key, stat2Key, stat1Label, stat2Label] = getStatLabel(position);

  return (
    <div className="relative w-full aspect-[3/4] max-w-sm mx-auto">
      <div className={`absolute inset-0 bg-gradient-to-br from-gray-900 via-${clubColor}-900 to-${clubColor}-700 rounded-lg shadow-lg overflow-hidden`}>
        <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-black opacity-50"></div>
        <div className={`absolute top-0 right-0 text-9xl font-bold text-${clubColor}-500 opacity-10 z-10`}>
          {player.playerNumber}
        </div>
        <div className="relative z-2 p-4 h-full flex flex-col justify-between">
          <div>
            <img
              src={player.imageUrl}
              alt={player.fullName}
              className="w-full h-56 object-cover object-top rounded-lg"
            />
            <h3
              className="text-xl font-semibold text-white mt-4 cursor-pointer hover:underline"
              onClick={() => navigate(`/player/${encodeURIComponent(player.fullName)}`)}
            >
              {player.fullName}
            </h3>
          </div>
          <div className="grid grid-cols-3 gap-2 bg-black bg-opacity-30 p-2 rounded-lg mt-2">
            <StatBox label="MATCHES PLAYED" value={player.stats?.matchesPlayed ?? 0} />
            <StatBox label={stat1Label} value={player.stats?.[stat1Key] ?? 0} />
            <StatBox label={stat2Label} value={player.stats?.[stat2Key] ?? 0} />
          </div>
        </div>
      </div>
    </div>
  );
};

const StatBox = ({ label, value }) => (
  <div className="text-center">
    <p className="text-blue-300 font-semibold text-xs">{label}</p>
    <p className="text-2xl font-bold text-white">{value}</p>
  </div>
);

const PositionGroup = ({ title, players, clubColor }) => (
  <div className="mb-10">
    <h2 className={`text-2xl font-bold text-${clubColor}-300 mb-4`}>{title}</h2>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {players.map((player) => (
        <PlayerCard key={player._id} player={player} position={title} clubColor={clubColor} />
      ))}
    </div>
  </div>
);

const CoachCard = ({ coach, clubColor }) => (
  <div className={`bg-${clubColor}-800 rounded-lg shadow-lg p-4 m-2 relative overflow-hidden transform transition-all duration-300 hover:scale-105`}>
    <img
      src={coach.imageUrl}
      alt={coach.fullName}
      className="w-full h-64 object-cover object-top rounded-lg"
    />
    <h3 className={`text-xl font-semibold text-${clubColor}-300 mt-4`}>{coach.fullName}</h3>
    <p className="text-gray-400">Coach</p>
  </div>
);

const Squad = ({ clubId, clubColor }) => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const backend = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!clubId) return;
    const fetchPlayers = async () => {
      try {
        const res = await axios.get(`${backend}/scrape/players/${clubId}`);
        setPlayers(res.data.players || []);
      } catch (error) {
        setPlayers([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPlayers();
  }, [clubId, backend]);

  if (loading) {
    return <div className="text-white">Loading squad...</div>;
  }

  if (!players.length) {
    return <div className="text-white">No squad data available for this club yet.</div>;
  }

  const byPosition = (position) => players.filter((p) => p.position === position);
  const coach = players.find((p) => p.position === "HEAD COACH");

  return (
    <div className={`max-w-7xl mx-auto px-4 py-8 font-sans bg-${clubColor}-900 text-white`}>
      {["GOALKEEPERS", "DEFENDERS", "MIDFIELDERS", "FORWARDS"].map((position) => {
        const positionPlayers = byPosition(position);
        return positionPlayers.length > 0 ? (
          <PositionGroup key={position} title={position} players={positionPlayers} clubColor={clubColor} />
        ) : null;
      })}

      {coach && (
        <div className="mb-10">
          <h2 className={`text-2xl font-bold text-${clubColor}-300 mb-4`}>COACH</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <CoachCard coach={coach} clubColor={clubColor} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Squad;
